// @flow
import React, { Component } from "react";
import withStyles from "elevate-ui/withStyles";
import Paper from "elevate-ui/Paper";
import NumberIncrement from "elevate-ui/NumberIncrement";
import CurrencyInput from "elevate-ui/CurrencyInput";
import ButtonGroup from "elevate-ui/ButtonGroup";
import Tooltip from "elevate-ui/Tooltip";
import Hr from "elevate-ui/Hr";
import Typography from "elevate-ui/Typography";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import classNames from "classnames";

import KeyboardArrowDown from "elevate-ui-icons/KeyboardArrowDown";
import KeyboardArrowUp from "elevate-ui-icons/KeyboardArrowUp";
import InfoOutline from "elevate-ui-icons/InfoOutline";

import mortgageJs from "./MortgageCalculator/mortgage-calculator";
import DownPayment from "./MortgageCalculator/DownPayment";

type $Props = {
  classes: Object,
  className?: Object,
  withPaper?: boolean,
  totalPrice?: number,
  datastoreProviderId?: number | string,
};

type $State = {
  showAdvanced: boolean,
};

class Calculator extends Component<$Props, $State> {
  constructor(props) {
    super(props);

    this.state = {
      showAdvanced: false,
    };
  }

  toggleAdvanced = () => {
    this.setState((state) => ({ showAdvanced: !state.showAdvanced }));
  };

  calculatePayment = (values, datastoreProviderId) => {
    const payment = mortgageJs.calculatePayment(
      values.totalPrice,
      values.downPayment,
      values.interestRate * 0.01,
      values.months,
      values.taxRate * 0.01,
      values.insuranceRate * 0.01,
      values.mortgageInsuranceRate * 0.01,
      values.mortgageInsuranceEnabled === "true" ? true : false,
      0.2,
      values.additionalPrincipalPayment,
      datastoreProviderId
    ).total;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(payment);
  };

  handleMortgageInsurance = (values) => {
    const { totalPrice, downPayment } = values;
    const percentDown = (downPayment / totalPrice) * 100;

    if (percentDown >= 20) {
      return this.props.classes.hide;
    }
  };

  render() {
    const {
      classes,
      className,
      withPaper = true,
      totalPrice,
      datastoreProviderId,
    } = this.props;
    const { showAdvanced } = this.state;

    const calculator = (
      <div>
        <Formik
          initialValues={{
            totalPrice: totalPrice ? totalPrice : 450000,
            downPayment: totalPrice ? totalPrice * 0.2 : 90000,
            interestRate: 5.2,
            months: "360",
            taxRate: 1.2,
            insuranceRate: 0.13,
            mortgageInsuranceRate: 1,
            mortgageInsuranceEnabled: "true",
            additionalPrincipalPayment: 0,
          }}
          validationSchema={() =>
            Yup.object().shape({
              totalPrice: Yup.number().required("Total home price required."),
              downPayment: Yup.number().required("Down payment required."),
              interestRate: Yup.number().required("Interest rate required."),
              months: Yup.number().required("Loan program required."),
              taxRate: Yup.number().required("Tax rate required."),
              insuranceRate: Yup.number().required("Insurance Rate required."),
              mortgageInsuranceRate: Yup.number().required(
                "Mortgage Insurance Rate required."
              ),
              additionalPrincipalPayment: Yup.number(),
            })
          }
          enableReinitialize
          render={({ values }) => (
            <Form id="mortgageCalculator" noValidate>
              <Typography type="heading2" gutterTop gutterBottom>
                Mortgage Calculator
              </Typography>
              <div className={classes.formFieldContainer}>
                <div className={classes.span2}>
                  <Field
                    name="totalPrice"
                    id="totalPrice"
                    label="Total Home Price"
                    component={CurrencyInput}
                    precision={0}
                  />
                </div>
                <div className={classes.span2}>
                  <Field
                    name="downPayment"
                    id="downPayment"
                    label="Down Payment"
                    component={DownPayment}
                  />
                </div>
                <div className={classes.span1}>
                  <Field
                    name="interestRate"
                    id="interestRate"
                    label="Interest Rate (%)"
                    component={NumberIncrement}
                    step={0.1}
                    className={classes.percent}
                  />
                </div>
                <div className={classes.span3}>
                  <Field
                    type="number"
                    name="months"
                    id="months"
                    label="Loan Program"
                    component={ButtonGroup}
                    items={[
                      { value: "360", label: "30-Year Fixed" },
                      { value: "180", label: "15-Year Fixed" },
                    ]}
                  />
                </div>
                <div className={classes.span4}>
                  <button
                    className={classes.advanced}
                    type="button"
                    onClick={this.toggleAdvanced}
                  >
                    {showAdvanced ? "Hide Advanced " : "Show Advanced "}
                    {showAdvanced ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </button>
                </div>
                <div
                  className={classNames(
                    classes.span2,
                    !showAdvanced && classes.hide
                  )}
                >
                  <Field
                    name="taxRate"
                    id="taxRate"
                    label="Tax Rate (%)"
                    component={NumberIncrement}
                    step={0.1}
                    className={classes.percent}
                  />
                </div>
                <div
                  className={classNames(
                    classes.span2,
                    !showAdvanced && classes.hide
                  )}
                >
                  <Field
                    name="insuranceRate"
                    id="insuranceRate"
                    label="Insurance Rate (%)"
                    step={0.1}
                    component={NumberIncrement}
                    className={classes.percent}
                  />
                </div>
                <div
                  className={classNames(
                    classes.span2,
                    !showAdvanced && classes.hide,
                    this.handleMortgageInsurance(values)
                  )}
                >
                  <Field
                    name="mortgageInsuranceEnabled"
                    id="mortgageInsuranceEnabled"
                    label={
                      <Tooltip
                        text="PMI is generally required for loans with less then 20% down"
                        className={classes.tooltip}
                      >
                        <div className={classes.labelWithIcon}>
                          With Mortgage Insurance <InfoOutline />
                        </div>
                      </Tooltip>
                    }
                    items={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                    component={ButtonGroup}
                  />
                </div>
                <div
                  className={classNames(
                    classes.span2,
                    !showAdvanced && classes.hide,
                    this.handleMortgageInsurance(values)
                  )}
                >
                  <Field
                    name="mortgageInsuranceRate"
                    id="mortgageInsuranceRate"
                    label="Mortgage Insurance Rate (%)"
                    component={NumberIncrement}
                    step={0.1}
                    className={classes.percent}
                  />
                </div>
                <div
                  className={classNames(
                    classes.span2,
                    !showAdvanced && classes.hide
                  )}
                >
                  <Field
                    id="additionalPrincipalPayment"
                    name="additionalPrincipalPayment"
                    label="Additional Principal Payment"
                    component={CurrencyInput}
                  />
                </div>
                <div className={classes.span4}>
                  <Hr />
                  <Typography type="heading5" gutterTop gutterBottom>
                    Monthly Payment:{" "}
                    {this.calculatePayment(values, datastoreProviderId)}
                  </Typography>
                </div>
              </div>
            </Form>
          )}
        />
      </div>
    );

    if (withPaper) {
      return <Paper className={className}>{calculator}</Paper>;
    } else {
      return <div className={className}>{calculator}</div>;
    }
  }
}

const styles = (theme) => ({
  formFieldContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gridColumnGap: "12px",
    gridRowGrap: "12px",
    ".ie-11 &": {
      display: "flex",
      flexFlow: "row wrap",
      "& > div": {
        padding: "0 6px",
      },
    },
  },
  span1: {
    gridColumn: "span 1",
  },
  span2: {
    gridColumn: "span 4",
    [theme.breakpoints(644)]: {
      gridColumn: "span 2",
    },
  },
  span3: {
    gridColumn: "span 4",
    [theme.breakpoints(490)]: {
      gridColumn: "span 3",
    },
  },
  span4: {
    gridColumn: "span 4",
    ".ie-11 &": {
      flex: "1 1 100%",
    },
  },
  percent: {
    alignItems: "stretch",
    height: "46px",
    "& button, input": {
      height: "auto",
    },
    "& input": {
      width: "85px",
    },
  },
  advanced: {
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    margin: "24px 0",
    ".ie-11 &": {
      flex: "1 1 100%",
    },
  },
  hide: {
    display: "none",
  },
  labelWithIcon: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  tooltip: {
    "&:after": {
      left: 0,
    },
    "&:before": {
      left: "10px",
    },
  },
});

export default withStyles(styles)(Calculator);
